import "../assets/scss/components/_search-bar.scss";
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faClose } from "@fortawesome/pro-solid-svg-icons/faClose";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons/faMagnifyingGlass";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { instantMeiliSearch } from "@meilisearch/instant-meilisearch";
import { useState, useEffect, useCallback, Suspense } from "react";
import { InstantSearch, useSearchBox, useHits, useInstantSearch, InstantSearchSSRProvider } from "react-instantsearch";
import { useNavigate, Link, useParams, useMatches } from "react-router";

import Config from "../Config";
import { useCurrentLocation } from "../contexts/LocationContext";
import type { SearchBarInnerProps, SearchBarProps } from "../types/components/SearchBarProps";
import { Image } from "./Image";

// Create search client
const { searchClient } = instantMeiliSearch(Config.MEILISEARCH_HOST, Config.MEILISEARCH_API_KEY, {
	placeholderSearch: false,
	meiliSearchParams: {
		filter: 'stock > 0 AND status = "Enabled"',
	},
});

const SearchBarInner = ({ closeNav, instantSearch, mobile, ...props }: SearchBarInnerProps) => {
	const params = useParams();
	const navigate = useNavigate();
	const location = useCurrentLocation();

	const [searchBar, setSearchBar] = useState(false);

	const { query, refine } = useSearchBox(props);
	const { items } = useHits();
	const { status } = useInstantSearch();

	const showLoader = status === "stalled" || status === "loading";

	useEffect(() => {
		if (params?.search) {
			refine(params.search as string);
		}
	}, [params?.search, location.pathname, refine]);

	const handleSubmit = useCallback(() => {
		if (query) {
			navigate(`/search/${query}`);
			closeNav();
			setSearchBar(false);
		} else {
			setSearchBar((prev) => !prev);
		}
	}, [query, navigate, closeNav]);

	return (
		<div
			className={`search-bar-container col-4 col-md-4 ${searchBar ? "active" : ""} ${mobile ? "mobile" : ""}`}
			style={{ marginRight: "auto", marginLeft: "auto" }}
		>
			<div className="field" style={{ border: "none" }}>
				<input
					type="text"
					placeholder=""
					value={query}
					style={{ background: "#F4F4F4" }}
					onChange={(e) => refine(e.target.value)}
					aria-label="Search For Products"
					onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
					onFocus={() => setSearchBar(true)}
					onBlur={() => setTimeout(() => setSearchBar(false), 200)}
				/>
				<label htmlFor="">Search For Products</label>
				{showLoader && (
					<div className="loader">
						<div className="inner-loader" />
					</div>
				)}
			</div>

			<div className={`results-container ${searchBar ? "active" : ""}`}>
				{items.length > 0 && (
					<div className="search-results">
						{query && <div className="search-status">Search Results For "{query}"</div>}
						{items.map((product: any) => (
							<Link
								to={`/${product.uri}`}
								className="search-result row justify-content-space-between align-items-center"
								key={product.id || product.products_id}
							>
								<div className="col-2 image-container">
									<Image width={100} src={product.full_image_url} alt={product.products_name} />
								</div>
								<div className="col-10 description-container">
									<h4>
										{product.products_name} ({Config.currency}
										{(
											parseFloat(product.price_retail) +
											(product.bundle_price ? parseFloat(product.bundle_price) : 0)
										).toFixed(2)}
										)
									</h4>
									{product.short_description && <p>{product.short_description}</p>}
								</div>
							</Link>
						))}
					</div>
				)}
			</div>

			{!instantSearch && (
				<button className="search-button" aria-label="Search" onClick={handleSubmit}>
					<FontAwesomeIcon icon={searchBar ? (query === "" ? faClose : faCheck) : faMagnifyingGlass} />
				</button>
			)}
		</div>
	);
};

export const InstantSearchBar = ({
	serverUrl,
	serverState,
	closeNav,
	instantSearch = true,
	mobile = false,
	...props
}) => {
	return (
		<InstantSearchSSRProvider {...serverState}>
			<InstantSearch
				searchClient={searchClient}
				indexName={`products-${Config.SITE_ID}:sales:desc`}
				future={{ preserveSharedStateOnUnmount: true }}
			>
				<SearchBarInner closeNav={closeNav} instantSearch={instantSearch} mobile={mobile} {...props} />
			</InstantSearch>
		</InstantSearchSSRProvider>
	);
};

export const SearchBar = (props: SearchBarProps) => {
	const matches = useMatches();
	const { serverState, serverUrl } = matches.find((match) => match.id === "root")?.data || {};
	return <InstantSearchBar serverState={serverState} serverUrl={serverUrl} {...props} />;
};

const SearchBarSSRInner = () => {
	useSearchBox();
	useHits();
	return null;
};

export const SearchBarSSR = ({ serverUrl }: { serverUrl?: URL | string }) => {
	return (
		<InstantSearch
			searchClient={searchClient}
			indexName={`products-${Config.SITE_ID}:sales:desc`}
			future={{ preserveSharedStateOnUnmount: true }}
		>
			<SearchBarSSRInner />
		</InstantSearch>
	);
};
